<template>
  <div class="min-h-screen flex flex-col items-center justify-center">
    <div class="m-16"></div>
    <Export @export-png="exportToPNG" class="mb-8" />
    <div id="pdf-area" class="w-full flex flex-col items-center">
      <canvas id="radarCanvas" ref="radarCanvas" class="w-full max-w-6xl h-[600px]"></canvas>
    </div>
    <div class="w-full max-w-6xl mt-8 space-y-8">
      <CategoryTable />
      <StakeholderTable />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { RadarChart } from '../../../stakeholdersMap/core/RadarChart.ts';
import { storeToRefs } from 'pinia';
import html2canvas from 'html2canvas';
import StakeholderTable from './StakeholderTable.vue';
import CategoryTable from './CategoryTable.vue';
import Export from './Export.vue';
import { createStakeholderMapStore } from '../stores/index.ts';

const route = useRoute();
const radarCanvas = ref<HTMLCanvasElement | null>(null);

// Use the factory to get the appropriate store based on environment
const stakeholderMapStore = createStakeholderMapStore();
const { categories } = storeToRefs(stakeholderMapStore);

onMounted(async () => {
  // Get map ID from route query parameter, or use default
  const mapId = (route.query.id as string);
  await stakeholderMapStore.fetchStakeholderMap(mapId);
});

// Redraw chart when categories change
watch(categories, () => {
  if (radarCanvas.value) {
    new RadarChart(categories.value).draw();
  }
}, { deep: true });

const exportToPNG = async () => {
  const element = document.getElementById('pdf-area');
  if (!element) return;

  const canvas = await html2canvas(element, {
    scale: 2,
    backgroundColor: '#ffffff',
    useCORS: true,
    allowTaint: true
  });
  
  // Add diagonal watermark
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.save();
    ctx.globalAlpha = 0.2; // Semi-transparent
    ctx.font = 'bold 48px Arial';
    ctx.fillStyle = '#666666';
    
    // Center the text on the diagonal
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    
    // Rotate canvas to draw text diagonally
    ctx.translate(centerX, centerY);
    ctx.rotate(-Math.PI / 4); // -45 degrees
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('GraphRM', 0, 0);
    
    ctx.restore();
  }

  const imgData = canvas.toDataURL('image/png');
  const link = document.createElement('a');
  link.download = 'chart.png';
  link.href = imgData;
  link.click();
};
</script>